import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, Building2, Home, Car, Gift, Plus, Trash2 } from 'lucide-react';

const SERVICE_TYPES = [
  { id: 'salon', name: 'Salon / Studio', icon: Building2, description: 'Clients come to your fixed location' },
  { id: 'home', name: 'Home-based', icon: Home, description: 'You work from home' },
  { id: 'mobile', name: 'Mobile', icon: Car, description: 'You travel to clients' }
];

const RADIUS_OPTIONS = [1, 3, 5, 10, 15, 20, 25, 30];

export function ServiceTypeStep({ form, setForm, onServiceTypeToggle }) {
  const addTravelTier = () => {
    setForm({ ...form, travel_charges: [...(form.travel_charges || []), { max_miles: '', charge: '' }] });
  };

  const removeTravelTier = (idx) => {
    setForm({ ...form, travel_charges: (form.travel_charges || []).filter((_, i) => i !== idx) });
  };

  const updateTravelTier = (idx, field, value) => {
    const updated = [...(form.travel_charges || [])];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, travel_charges: updated });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">How do you offer your services?</h2>
        <p className="text-stone-500">Select at least one option. This helps clients find you based on their preferences.</p>
      </div>

      <div className="space-y-4" data-testid="service-types-grid">
        {SERVICE_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => onServiceTypeToggle(type.id)}
            className={`w-full p-6 rounded-xl border text-left transition-all ${
              form.service_types.includes(type.id)
                ? 'border-[#D4AF37] bg-[#F9F6F0] ring-2 ring-[#D4AF37]'
                : 'border-[#E6E4DD] hover:border-[#D4AF37]'
            }`}
            data-testid={`service-type-${type.id}`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                form.service_types.includes(type.id) ? 'bg-[#D4AF37] text-white' : 'bg-[#F9F6F0] text-[#D4AF37]'
              }`}>
                <type.icon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#1A1A1A] text-lg">{type.name}</h3>
                <p className="text-sm text-stone-500">{type.description}</p>
              </div>
              {form.service_types.includes(type.id) && (
                <CheckCircle className="h-6 w-6 text-[#D4AF37] ml-auto flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Salon/Studio Address */}
      {form.service_types.includes('salon') && (
        <div className="p-5 bg-[#F9F6F0] rounded-lg space-y-3 animate-fadeIn" data-testid="salon-address-section">
          <Label className="text-[#1A1A1A] flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#D4AF37]" />
            Salon / Studio Address *
          </Label>
          <p className="text-xs text-stone-500">This address will be visible to clients so they know where to come.</p>
          <div className="space-y-2">
            <div>
              <Label htmlFor="salon-line1" className="text-xs text-stone-600">Building Number & Street *</Label>
              <Input
                id="salon-line1"
                value={typeof form.salon_address === 'object' ? (form.salon_address?.line1 || '') : (form.salon_address || '')}
                onChange={(e) => setForm({...form, salon_address: {...(typeof form.salon_address === 'object' ? form.salon_address : {}), line1: e.target.value}})}
                placeholder="e.g. 42 Brixton Road"
                className="input-zuriya"
                data-testid="salon-address-line1"
              />
            </div>
            <div>
              <Label htmlFor="salon-line2" className="text-xs text-stone-600">Unit / Suite (optional)</Label>
              <Input
                id="salon-line2"
                value={typeof form.salon_address === 'object' ? (form.salon_address?.line2 || '') : ''}
                onChange={(e) => setForm({...form, salon_address: {...(typeof form.salon_address === 'object' ? form.salon_address : {}), line2: e.target.value}})}
                placeholder="e.g. Unit 3"
                className="input-zuriya"
                data-testid="salon-address-line2"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="salon-city" className="text-xs text-stone-600">City *</Label>
                <Input
                  id="salon-city"
                  value={typeof form.salon_address === 'object' ? (form.salon_address?.city || '') : ''}
                  onChange={(e) => setForm({...form, salon_address: {...(typeof form.salon_address === 'object' ? form.salon_address : {}), city: e.target.value}})}
                  placeholder="e.g. your city"
                  className="input-zuriya"
                  data-testid="salon-address-city"
                />
              </div>
              <div>
                <Label htmlFor="salon-postcode" className="text-xs text-stone-600">Postcode *</Label>
                <Input
                  id="salon-postcode"
                  value={typeof form.salon_address === 'object' ? (form.salon_address?.postcode || '') : ''}
                  onChange={(e) => setForm({...form, salon_address: {...(typeof form.salon_address === 'object' ? form.salon_address : {}), postcode: e.target.value.toUpperCase()}})}
                  placeholder="e.g. SW9 8LQ"
                  className="input-zuriya"
                  data-testid="salon-address-postcode"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Home-based Address */}
      {form.service_types.includes('home') && (
        <div className="p-5 bg-[#F9F6F0] rounded-lg space-y-3 animate-fadeIn" data-testid="home-address-section">
          <Label className="text-[#1A1A1A] flex items-center gap-2">
            <Home className="h-4 w-4 text-[#D4AF37]" />
            Home Address *
          </Label>
          <p className="text-xs text-stone-500">
            Only the area will be shown to clients before booking. The full address is revealed after booking confirmation.
          </p>
          <div className="space-y-2">
            <div>
              <Label htmlFor="home-line1" className="text-xs text-stone-600">House/Flat Number & Street *</Label>
              <Input
                id="home-line1"
                value={typeof form.home_address === 'object' ? (form.home_address?.line1 || '') : (form.home_address || '')}
                onChange={(e) => setForm({...form, home_address: {...(typeof form.home_address === 'object' ? form.home_address : {}), line1: e.target.value}})}
                placeholder="e.g. 15 Atlantic Road"
                className="input-zuriya"
                data-testid="home-address-line1"
              />
            </div>
            <div>
              <Label htmlFor="home-line2" className="text-xs text-stone-600">Flat/Unit (optional)</Label>
              <Input
                id="home-line2"
                value={typeof form.home_address === 'object' ? (form.home_address?.line2 || '') : ''}
                onChange={(e) => setForm({...form, home_address: {...(typeof form.home_address === 'object' ? form.home_address : {}), line2: e.target.value}})}
                placeholder="e.g. Flat 2B"
                className="input-zuriya"
                data-testid="home-address-line2"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="home-city" className="text-xs text-stone-600">City *</Label>
                <Input
                  id="home-city"
                  value={typeof form.home_address === 'object' ? (form.home_address?.city || '') : ''}
                  onChange={(e) => setForm({...form, home_address: {...(typeof form.home_address === 'object' ? form.home_address : {}), city: e.target.value}})}
                  placeholder="e.g. your city"
                  className="input-zuriya"
                  data-testid="home-address-city"
                />
              </div>
              <div>
                <Label htmlFor="home-postcode" className="text-xs text-stone-600">Postcode *</Label>
                <Input
                  id="home-postcode"
                  value={typeof form.home_address === 'object' ? (form.home_address?.postcode || '') : ''}
                  onChange={(e) => setForm({...form, home_address: {...(typeof form.home_address === 'object' ? form.home_address : {}), postcode: e.target.value.toUpperCase()}})}
                  placeholder="e.g. SW9 8HX"
                  className="input-zuriya"
                  data-testid="home-address-postcode"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Travel Radius + Travel Charges */}
      {form.service_types.includes('mobile') && (
        <div className="p-5 bg-[#F9F6F0] rounded-lg space-y-4 animate-fadeIn" data-testid="mobile-radius-section">
          <div className="space-y-2">
            <Label htmlFor="service_radius" className="text-[#1A1A1A] flex items-center gap-2">
              <Car className="h-4 w-4 text-[#D4AF37]" />
              How far will you travel? *
            </Label>
            <p className="text-xs text-stone-500">Select the maximum distance you're willing to travel to a client.</p>
            <Select
              value={form.service_radius ? String(form.service_radius) : ''}
              onValueChange={(val) => setForm({...form, service_radius: parseInt(val)})}
            >
              <SelectTrigger className="input-zuriya max-w-[240px]" data-testid="radius-select">
                <SelectValue placeholder="Select distance" />
              </SelectTrigger>
              <SelectContent>
                {RADIUS_OPTIONS.map((miles) => (
                  <SelectItem key={miles} value={String(miles)}>
                    Up to {miles} {miles === 1 ? 'mile' : 'miles'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Free travel toggle (all travel free regardless of distance). */}
          <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E6E4DD] bg-white cursor-pointer hover:border-green-400 transition-colors" data-testid="free-travel-toggle">
            <Checkbox
              checked={form.free_travel || false}
              onCheckedChange={(checked) => setForm({ ...form, free_travel: !!checked })}
            />
            <Gift className="h-4 w-4 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Free travel within my whole radius</p>
              <p className="text-xs text-stone-400">No travel charge, anywhere I go. Overrides the tiers below.</p>
            </div>
          </label>

          {/* Hybrid mode, a free-travel zone plus paid tiers beyond it.
              Only shown when "all free" isn't enabled; combining the two
              would never make a difference to the client. */}
          {!form.free_travel && (
            <div className="space-y-3" data-testid="travel-charges-section">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#FFF8E7] border border-[#D4AF37]/30" data-testid="free-travel-zone-row">
                <Gift className="h-4 w-4 text-green-600 flex-shrink-0" />
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <span>Free travel up to</span>
                  <Input
                    type="number"
                    min="0"
                    max={form.service_radius || 50}
                    value={form.free_travel_miles || ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setForm({ ...form, free_travel_miles: raw === '' ? 0 : parseInt(raw) || 0 });
                    }}
                    className="input-zuriya text-sm w-20"
                    placeholder="0"
                    data-testid="free-travel-miles-input"
                  />
                  <span>miles</span>
                  <span className="text-xs text-stone-500">(optional, leave at 0 to charge from the first mile)</span>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-[#1A1A1A]">Travel charges beyond your free zone</p>
                <p className="text-xs text-stone-400">
                  Set prices based on how far you travel. For example, £5 up to 6 miles, £10 up to 10 miles. Clients will see their exact quote at booking.
                </p>
              </div>

              {(form.travel_charges || []).map((tier, idx) => (
                <div key={`tier-${idx}`} className="flex flex-wrap items-center gap-2 bg-white p-3 rounded-lg border border-[#E6E4DD]">
                  <span className="text-xs text-stone-500 whitespace-nowrap">Up to</span>
                  <Input
                    type="number"
                    min="1"
                    value={tier.max_miles || ''}
                    onChange={(e) => updateTravelTier(idx, 'max_miles', parseInt(e.target.value) || 0)}
                    className="input-zuriya text-sm w-16"
                    placeholder="mi"
                    data-testid={`tier-miles-${idx}`}
                  />
                  <span className="text-xs text-stone-500">miles</span>
                  <span className="text-sm text-stone-600 sm:ml-2">£</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.50"
                    value={tier.charge || ''}
                    onChange={(e) => updateTravelTier(idx, 'charge', parseFloat(e.target.value) || 0)}
                    className="input-zuriya text-sm w-20"
                    placeholder="0.00"
                    data-testid={`tier-charge-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeTravelTier(idx)}
                    className="p-1.5 text-red-400 hover:text-red-600 transition-colors ml-auto"
                    data-testid={`tier-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addTravelTier}
                className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1.5 py-1"
                data-testid="add-travel-tier-btn"
              >
                <Plus className="h-4 w-4" /> Add distance tier
              </button>

              {(form.travel_charges || []).length === 0 && !form.free_travel_miles && (
                <p className="text-xs text-stone-400 italic">No travel charges set, clients won't be charged extra for mobile services.</p>
              )}
            </div>
          )}
        </div>
      )}

      {form.service_types.length === 0 && (
        <p className="text-sm text-red-500">Please select at least one service type</p>
      )}
    </div>
  );
}
